import { ITransactionRequest, IBorrowerTransactionRequest, IBorrowerPairTransactionId } from "../models/ITransactionRequest";
import { IDeliveryType } from "../models/configuration/plugin/IConfiguration";
import { IDocListItem } from "../models/IDocListResponse";
import { 
    API_REQUEST_STATUS, 
    BUTTON_LABELS, 
    DELIVERY_PREFIX, 
    EPC_REQUEST_TYPES, 
    REQUEST_TYPES, 
    SCOPE_TYPE 
} from "../utils/constants";
import { store } from "../state/store";

export interface ITransactionBuilderOptions {
    requestType: string;
    packageId: string;
    parentTypeId?: string;
    altPackageId?: string;
    deliveryMethods: Array<IDeliveryType>;
    selectedDocuments?: Array<IDocListItem>;
    buttonText?: string;
    deliveryEmails?: Array<string>;
    collabEmails?: Array<string>;
    borrowerTransactionIds?: IBorrowerPairTransactionId[];
}

export default class TransactionBuilderService {
    
    /**
     * Validates that required loan and configuration data exists in the state
     */
    private static validateLoanAndConfig(): { isValid: boolean; error?: string } {
        const currentState = store.getState();
        
        if (!currentState.appSlice.isASO) {
            if (!currentState.appSlice.loanId) {
                return { isValid: false, error: "Loan ID is required to build transaction request" };
            }
            if (!currentState.appSlice.configId) {
                return { isValid: false, error: "Configuration ID is required to build transaction request" };
            }
        }

        return { isValid: true };
    }

    /**
     * Determines if the package is a closing package
     */
    private static isClosingPackage(packageId: string): boolean {
        const normalizedPackageId = packageId?.toLowerCase() ?? "";
        return normalizedPackageId.includes("closing") || normalizedPackageId.includes("shipping");
    }

    /**
     * Builds a complete transaction request object based on the provided options
     */
    public static buildTransactionObject(options: ITransactionBuilderOptions): ITransactionRequest | null {
        const currentState = store.getState();
        
        // Validate required data
        const validation = this.validateLoanAndConfig();
        if (!validation.isValid) {
            console.error(validation.error);
            return null;
        }

        const {
            requestType,
            packageId,
            parentTypeId = '',
            altPackageId = '',
            deliveryMethods = [],
            selectedDocuments = [],
            buttonText = '',
            deliveryEmails = [],
            collabEmails = [],
            borrowerTransactionIds = []
        } = options;

        // Determine preview and request type settings
        let enablePreview = false;
        let docGenRequestType = REQUEST_TYPES.DOCUMENT_REQUEST_IMPORT;
        
        if (requestType === REQUEST_TYPES.LIGHTS_OUT) {
            docGenRequestType = REQUEST_TYPES.DOCUMENT_REQUEST;
            enablePreview = deliveryMethods.length < 1;
        }

        // Determine delivery method flags
        const ronIndicator = this.hasDeliveryType(deliveryMethods, DELIVERY_PREFIX.ECLOSE, DELIVERY_PREFIX.RON);
        const eNoteIndicator = this.hasDeliveryType(deliveryMethods, DELIVERY_PREFIX.ECLOSE, DELIVERY_PREFIX.ENOTE);
        const eNotaryIndicator = this.hasDeliveryType(deliveryMethods, DELIVERY_PREFIX.ECLOSE, DELIVERY_PREFIX.IPEN);
        const eDiscloseIndicator = deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.EDISCLOSE)) ?? false;
        const eSignIndicator = deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN)) ?? false;
        const eCloseIndicator = deliveryMethods?.some(d => d.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE)) ?? false;

        // Extract custom attributes from delivery methods
        const iPenVendor = deliveryMethods.find(d => d.CustomAttributes?.IPENVendor)?.CustomAttributes?.IPENVendor ?? "";
        const eNotaryType = eNotaryIndicator ? iPenVendor : "";
        const verificationVendorType = deliveryMethods?.find(d => d.CustomAttributes?.VerificationVendorType)?.CustomAttributes?.VerificationVendorType ?? "";
        const formFreeLenderId = deliveryMethods?.find(d => d.CustomAttributes?.FormFreeLenderID)?.CustomAttributes?.FormFreeLenderID ?? "";
        const iFrameVendor = deliveryMethods?.find(d => d.CustomAttributes?.IframeVendor)?.CustomAttributes?.IframeVendor ?? "";

        // Determine transaction type
        let epcTransactionType = EPC_REQUEST_TYPES.DISCLOSURES;
        if (buttonText === BUTTON_LABELS.PREVIEW) {
            epcTransactionType = EPC_REQUEST_TYPES.PROCESSING;
        } else if (this.isClosingPackage(packageId)) {
            epcTransactionType = EPC_REQUEST_TYPES.CLOSING;
        }

        // Build the main transaction request
        const request: ITransactionRequest = {
            type: epcTransactionType,
            options: {
                RequestType: "DocGen",
                CustomAttributes: {
                    CxIframeVendor: iFrameVendor,
                    CxIPenVendor: iPenVendor,
                    CxDeliveryType: deliveryMethods?.map(e => e.Id)?.join(','),
                    CxFormFreeLenderID: formFreeLenderId,
                    CxVerificationVendorType: verificationVendorType
                },
                DocGenRequest: {
                    LoanId: currentState.appSlice.loanId ?? "",
                    ConfigurationId: currentState.appSlice.configId ?? "",
                    RequestOptions: {
                        RequestType: docGenRequestType,
                        PackageType: packageId,
                        AlternatePackageType: null,
                        BundlingType: "Merged",
                        IncludeDocumentData: (
                            currentState.appSlice.isASO
                                ? currentState.configASOPluginData?.configData
                                : currentState.configPlugInData?.data?.Configuration
                        )?.DefaultValues?.IncludeDocumentData ?? false,
                        EnablePreview: enablePreview,
                        Username: currentState.appSlice.userFullName ?? "",
                        PushbackOptions: {
                            PushbackEndpointUrl: process.env.REACT_APP_DT_PUSHBACK_API_URI as string
                        },
                        ImpersonationOption: {
                            ClientCode: currentState.appSlice.clientCode ?? "",
                            EnableImpersonation: currentState.appSlice.clientCode ? true : false
                        },
                    },
                    DeliveryOptions: {
                        Fulfillment: {
                            SendToFulfillment: false,
                            ShippingMethod: null
                        },
                    },
                },
                BorrowerPairTransactionIds: borrowerTransactionIds
            },
            resources: currentState.appSlice.attachments ?? []
        };

        // Add scope for SEND button
        if (buttonText === BUTTON_LABELS.SEND) {
            request.scope = {
                type: SCOPE_TYPE.LOAN,
            };
        }

        // Handle alternate/parent package ID
        if (altPackageId && altPackageId.trim() !== "") {
            const parsedAltPackageId = Number(packageId);
            if (!isNaN(parsedAltPackageId)) {
                request.options.DocGenRequest!.RequestOptions.AlternatePackageType = parsedAltPackageId;
                request.options.DocGenRequest!.RequestOptions.PackageType = parentTypeId;
            }
        }

        // Configure delivery options for non-LightsOn requests
        if (requestType !== REQUEST_TYPES.LIGHTS_ON) {
            request.options.DocGenRequest!.DeliveryOptions!.SolexEmail = null;
            request.options.DocGenRequest!.DeliveryOptions!.ClosingCollaboration = null;

            // Add email recipients if provided
            if (deliveryEmails != null && deliveryEmails.length > 0) {
                request.options.DocGenRequest!.DeliveryOptions!.SolexEmail = {
                    Recipients: deliveryEmails
                };
            }

            // Add collaboration recipients if provided
            if (collabEmails != null && collabEmails.length > 0) {
                request.options.DocGenRequest!.DeliveryOptions!.ClosingCollaboration = {
                    Recipients: collabEmails
                };
            }

            // Set delivery options flags
            request.options.DocGenRequest!.DeliveryOptions!.SolexEnotaryType = eNotaryType;
            request.options.DocGenRequest!.DeliveryOptions!.SolexVerificationVendorType = verificationVendorType;
            request.options.DocGenRequest!.DeliveryOptions!.SolexFormFreeLenderID = formFreeLenderId;
            request.options.DocGenRequest!.DeliveryOptions!.SolexEdisclose = eDiscloseIndicator;
            request.options.DocGenRequest!.DeliveryOptions!.SolexEsign = eSignIndicator;
            request.options.DocGenRequest!.DeliveryOptions!.SolexEclose = eCloseIndicator;
            request.options.DocGenRequest!.DeliveryOptions!.SolexEnote = eNoteIndicator;
            request.options.DocGenRequest!.DeliveryOptions!.SolexEnotary = eNotaryIndicator;
            request.options.DocGenRequest!.DeliveryOptions!.SolexRON = ronIndicator;

            // Handle mail delivery
            const mailDelivery = deliveryMethods.find(x => x.Id.toLowerCase().startsWith('mail'));
            if (mailDelivery) {
                request.options.DocGenRequest!.DeliveryOptions!.Fulfillment.SendToFulfillment = true;
                request.options.DocGenRequest!.DeliveryOptions!.Fulfillment.ShippingMethod = mailDelivery.Id.replace('Mail-', '');
            }

            // Add selected documents
            if (selectedDocuments.length > 0) {
                request.options.DocGenRequest!.RequestOptions.Documents = selectedDocuments.map(
                    (doc) => ({
                        DocumentIndex: doc.documentIndex ?? null,
                        PrimaryInstanceId: doc.primaryInstanceId ?? null,
                        SecondaryInstanceId: doc.secondaryInstanceId ?? null,
                        Copies: doc.copies ?? null
                    })
                );
            }

            // Add attachments
            if (request.resources && request.resources.length > 0) {
                request.options.DocGenRequest!.RequestOptions!.Attachments = request.resources.map(
                    (att) => ({
                        Name: att.name,
                        Type: att.name.split(".").pop() || null
                    })
                );
            }
        }

        return request;
    }

    /**
     * Builds a borrower transaction request object
     */
    public static buildBorrowerTransactionObject(borrowerPairId: string): IBorrowerTransactionRequest | null {
        const validation = this.validateLoanAndConfig();
        if (!validation.isValid) {
            console.error(validation.error);
            return null;
        }

        const request: IBorrowerTransactionRequest = {
            type: EPC_REQUEST_TYPES.DISCLOSURES,
            scope: {
                type: SCOPE_TYPE.APPLICATION,
                id: borrowerPairId
            }
        };

        return request;
    }

    /**
     * Helper method to check if delivery methods contain specific types
     */
    private static hasDeliveryType(
        deliveryMethods: Array<IDeliveryType>,
        primaryPrefix: string,
        secondaryPrefix?: string
    ): boolean {
        const filteredTypes = deliveryMethods?.filter(d => 
            d.Id.toLowerCase().startsWith(primaryPrefix)
        );
        
        if (!secondaryPrefix || filteredTypes.length === 0) {
            return filteredTypes.length > 0;
        }

        return filteredTypes.some(d => d.Id.toLowerCase().includes(secondaryPrefix));
    }
}
